import moment from 'moment/moment'

import { IconName } from '../components/Icon'
import { PSApplication } from '../components/app/PSApplication'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { Customizable } from './Customizable'
import { Field } from './Field'
import { Image } from './Image'
import { Mappable } from './Mappable'
import { Person } from './Person'
import { RecordDataMap } from './RecordDataMap'
import { RecordFactory } from './RecordFactory'
import { FKType } from './SQLBuilder'
import { Sortable } from './Sortable'
import { User } from './User'

type SubaccountConstructorArgs = Omit<ConstructorArgs<() => Subaccount>, 'birth_day'> & {
    birthday: moment.Moment | string | null
}

export type SubaccountType = 'none' | 'child' | 'pet' | 'property' | 'vehicle' | 'horse'
export type SubaccountImageType = 'default' | 'no-background' | 'blue-background'

export class Subaccount extends BaseRecord implements Customizable, Mappable, Sortable {
    readonly recordType = 'subaccount'

    readonly iCustomizable = true
    readonly iMappable = true
    readonly iSortable = true

    name?: string
    memo?: string
    type: SubaccountType
    image?: Image
    birthDay?: moment.Moment

    line1?: string
    line2?: string
    city?: string
    state?: string
    postalCode?: string
    countryCode?: string
    longitude?: number
    latitude?: number

    cancelledDate?: moment.Moment
    lastModifiedDate?: moment.Moment
    fieldValues: RecordDataMap = new RecordDataMap()

    constructor(data?: Partial<SubaccountConstructorArgs>, params?: { owner?: User }) {
        super(data)

        if (params?.owner) this.owner = params.owner

        this.type = data?.type ?? 'none'

        if (!data) {
            this.fieldValues = new RecordDataMap()
            this.creator = PSApplication.me()
            this.employee = this.creator?.employee
            this.owner = this.creator?.owner
            return
        }

        this.birthDay = this.propertyFromDateArg(data.birthday)
        this.cancelledDate = this.propertyFromTimestampArg(data.cancelled_date)
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
        this.name = data.name ?? undefined
        this.memo = data.memo ?? undefined
        this.line1 = data.line1 ?? undefined
        this.line2 = data.line2 ?? undefined
        this.city = data.city ?? undefined
        this.state = data.state ?? undefined
        this.postalCode = data.postal_code ?? undefined
        this.countryCode = data.country_code ?? undefined
        this.fieldValues = Field.loadFromRecordMap(
            new RecordDataMap(data),
            data.owner ? (data.owner instanceof User ? data.owner.id : data.owner) : undefined
        )
        this.image = this.propertyFromImageArg(data.image, data.image_uri)
    }

    clone(overrides?: Partial<SubaccountConstructorArgs>): Subaccount {
        return new Subaccount({
            ...this.serializeForClone(),
            birthday: this.birthDay,
            ...overrides,
        })
    }

    static initWithOwner(owner: User, type: SubaccountType): Subaccount {
        let subaccount = new Subaccount({ owner: owner })
        if (type !== 'none') {
            subaccount.type = type
        }
        return subaccount
    }

    get location(): string {
        return Person.locationString(
            this.line1,
            this.line2,
            this.city,
            this.state,
            this.postalCode,
            undefined
        )!
    }

    static iconNameForSubaccountType(type: SubaccountType): IconName {
        switch (type) {
            case 'pet': {
                return 'bone'
            }
            case 'property': {
                return 'briefcase'
            }
            case 'child': {
                return 'faceSmile'
            }
            case 'vehicle': {
                return 'car'
            }
            case 'horse': {
                return 'horse'
            }
            case 'none': {
                return 'user'
            }
        }
    }

    get labelString(): string {
        return Subaccount.labelString(this.type, false)
    }

    static labelString(type: SubaccountType, pluralize: boolean): string {
        switch (type) {
            case 'pet':
                return pluralize ? 'Pets' : 'Pet'
            case 'property':
                return pluralize ? 'Properties' : 'Property'
            case 'child':
                return pluralize ? 'Relatives' : 'Relative'
            case 'vehicle':
                return pluralize ? 'Vehicles' : 'Vehicle'
            case 'horse':
                return pluralize ? 'Horses' : 'Horse'
            case 'none':
                return pluralize ? 'Subaccounts' : 'Subaccount'
        }
    }

    get initials(): string {
        const lastName = Person.lastName(this.name)
        const firstName = Person.firstName(this.name)
        let initials = ''
        if (lastName) initials = `${firstName?.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
        else if (firstName) initials = `${firstName.charAt(0)}`.toUpperCase()
        for (const c of initials) {
            if (c >= 'A' && c <= 'z') return initials
        }
        return '#'
    }

    get initialsTextColor(): number {
        if (this.id === undefined) return 0
        return Person.initialsTextColor(this.id)
    }

    get initialsBackgroundColor(): number {
        if (this.id === undefined) return 0
        return Person.initialsBackgroundColor(this.id)
    }

    async fields(): Promise<Field[]> {
        return await RecordFactory.getInstance().loadSubaccountFields(
            this.owner!.id!,
            PSApplication.role() === User.PERM_ROLE_CLIENT
        )
    }

    get person(): User | undefined {
        const creator = this.creator
        const employee = this.employee
        const me = PSApplication.me().id
        if (me === this.client?.id)
            return employee ?? (creator && me !== creator.id ? creator : this.owner)
        return this.client
    }

    get date(): moment.Moment | undefined {
        return undefined
    }

    get duration(): number | undefined {
        return undefined
    }

    get title(): string | undefined {
        return this.labelString
    }

    get shortTitle(): string | undefined {
        return this.title
    }

    get coordinate(): [number, number] | undefined {
        if (this.latitude !== undefined && this.longitude !== undefined)
            return [this.latitude, this.longitude]
        return undefined
    }

    get sortKey(): string | undefined {
        const sortKey = this.client?.sortKey
        return `${sortKey}:${Person.sortName(this.name)}`
    }

    get sortName(): string | undefined {
        const sortName = this.client?.sortName
        return `${sortName}:${Person.sortName(this.name)}`
    }

    deserialize(data: any) {
        super.deserialize(data)
        if (data.client) this.client = BaseRecord.fromSerializer(data.client, User)
        if (data.owner) this.owner = BaseRecord.fromSerializer(data.owner, User)
        if (data.creator) this.creator = BaseRecord.fromSerializer(data.creator, User)
        if (data.employee) this.employee = BaseRecord.fromSerializer(data.employee, User)
        if (data.image) this.image = BaseRecord.fromSerializer(data.image, Image)
        if (data.birthDay) this.birthDay = moment(data.birthDay)
        if (data.cancelledDate) this.cancelledDate = moment(data.cancelledDate)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
    }

    static fk: FKType = {
        users: ['client', 'owner', 'creator', 'employee'],
        images: ['image'],
    }

    static dataType(): DataType {
        return 'subaccounts'
    }

    jsonObject(): any {
        const dict = super.jsonObject()
        dict['owner'] = this.owner?.id ?? ''
        dict['client'] = this.client?.id ?? ''
        dict['employee'] = this.employee?.id ?? ''
        dict['name'] = this.name ?? ''
        dict['memo'] = this.memo ?? ''
        dict['type'] = this.type
        dict['image'] = this.image?.id ?? ''
        dict['birthday'] = this.birthDay ? this.birthDay.format('YYYY-MM-DD') : ''
        dict['line1'] = this.line1 ?? ''
        dict['line2'] = this.line2 ?? ''
        dict['city'] = this.city ?? ''
        dict['postal_code'] = this.postalCode ?? ''
        dict['country_code'] = this.countryCode ?? ''
        if (this.fieldValues)
            dict['field_values'] = Field.serializeFieldData(
                this.fieldValues,
                this.owner?.id,
                undefined
            )
        return dict
    }

    get owner(): User | undefined {
        return this.getForeignRecord('owner', User)
    }
    set owner(owner: User | undefined) {
        this.setForeignRecord('owner', owner)
    }
    get client(): User | undefined {
        return this.getForeignRecord('client', User)
    }
    set client(client: User | undefined) {
        this.setForeignRecord('client', client)
    }
    get creator(): User | undefined {
        return this.getForeignRecord('creator', User)
    }
    set creator(creator: User | undefined) {
        this.setForeignRecord('creator', creator)
    }
    get employee(): User | undefined {
        return this.getForeignRecord('employee', User)
    }
    set employee(employee: User | undefined) {
        this.setForeignRecord('employee', employee)
    }
}
